import { useFieldArray, useForm, useWatch } from "react-hook-form";
import {
  DatePicker,
  Input,
  Label,
  MultipleSelect,
  Select,
  Submit,
  TextArea,
} from "../FormComponents";
import useLabels from "../../hooks/useLabels";
import { useNavigate, useParams } from "react-router";
import useAxiosPrivate from "../../hooks/useAxiosPrivate";
import { batch, useDispatch, useSelector } from "react-redux";
import {
  getDrugs,
  getOperations,
  getPomacesList,
  getProtocols,
  getUnits,
  getWineLabels,
  newOperation,
  updateOperation,
} from "../../services/utils";
import {
  ConfirmationModal,
  CTA,
  DestinationSelector,
  OperationRecap,
  PomacesDestination,
  SourceSelector,
  Tooltip,
} from "../Generic";
import {
  ALPHA_NUM_SPACE_DASH_UNDERSCORE_REGEX,
  INT_FLOAT_REGEX,
  INT_REGEX,
} from "../../constants/regex";
import { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import {
  selectAllLotNames,
  selectDrugs,
  selectOperations,
  selectTreatments,
  selectUnits,
  selectVirtualTanks,
  selectWineLabels,
} from "../../context/selectors";
import { setWineLabels } from "../../context/wineLabels/wineLabelsSlice";
import {
  add,
  empty,
  setOperations,
  updateOp,
} from "../../context/operations/operationsSlice";
import { emptyTanks } from "../../context/tanks/tanksSlice";
import { emptyLots } from "../../context/lots/lotsSlice";
import {
  assignDrugBatches,
  buildTreatmentsDataArray,
  computeRequiredNumericQty,
  computeRequiredWarehouseQty,
  getWarehouseUnit,
  groupBySubstanceId,
} from "../../services/common";
import TableEdit from "../TableEdit";
import { createColumnHelper } from "@tanstack/react-table";
import TableCell from "../TableEdit/TableCell";
import { setDrugs } from "../../context/substances/drugsSlice";
import { setTreatments } from "../../context/protocols/treatmentsSlice";
import { setUnits } from "../../context/units/unitsSlice";
import EditCell from "../TableEdit/EditCell";

const DestemmingForm = ({
  type,
  operation,
  lots,
  activeWinery,
  tanks,
  users,
  configs,
  isMobile,
  isDuplicate = false,
}) => {
  const { id } = useParams();
  const [getLabel] = useLabels();
  const navigate = useNavigate();
  const axiosPrivate = useAxiosPrivate();
  const dispatch = useDispatch();
  const wineLabels = useSelector(selectWineLabels);
  const [isOpen, setIsOpen] = useState(false);
  const [isExactDate, setIsExactDate] = useState(false);
  const [pomacesRecipients, setPomacesRecipients] = useState([]);
  // const [newBatchNames, setNewBatchNames] = useState(new Set());
  const operations = useSelector(selectOperations);
  const protocols = useSelector(selectTreatments);
  const drugs = useSelector(selectDrugs);
  const units = useSelector(selectUnits);
  const lotNames = useSelector(selectAllLotNames);
  // const [isPressed, setIsPressed] = useState([]);
  const [lotTypeMap, setLotTypeMap] = useState({});
  const [isBatchSelectorModal, setIsBatchSelectorModal] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isModifyMode, setIsModifyMode] = useState(-1);
  const [operationsP, setOperationsP] = useState([]);
  const [isEnoughStock, setIsEnoughStock] = useState(false);
  const prevProtocolIdRef = useRef(null);

  // const virtualTanks = useSelector(selectVirtualTanks);

  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
    setError,
    clearErrors,
    reset,
    setValue,
    getValues,
    resetField,
    control,
    trigger,
  } = useForm({
    defaultValues: {
      src_tanks: operations?.src_tanks,
      dest_tanks: operation?.dest_tanks || [
        {
          tankData: { value: null, label: null },
          quantity: 0,
          new_batch_name: "",
          batch_name_selector: false,
        },
      ],
      // batchType: operation?.batchType || "",
      expire_date: operation?.expire_date || null,
      type: "DESTEMMING",
      batch_id_in: operation?.batch_id_in || "",
      priority: operation?.priority || "",
      cellarman_ids: operation?.cellarman_ids || "",
      new_batch_name: operation?.new_batch_name || "",
    },
    mode: "onTouched",
  });

  const {
    register: modalRegister,
    reset: modalReset,
    handleSubmit: modalHandleSubmit,
    formState: { errors: modalErrors },
    setValue: modalSetValue,
    getValues: modalGetValues,
    setError: modalSetError,
    clearErrors: modalClearErrors,
    control: modalControl,
  } = useForm({
    defaultValues: {
      drug_batches_tmp: [{ value: null, label: null }],
      // day: protocol?.ay || "",
      // phase: protocol?.phase || "",
      // operation: protocol?.operation || "",
    },
    mode: "onTouched",
  });

  const { batch_id_in, dest_tanks, protocol_id, pomaces, pomacesW, drug_batches } = useWatch({
    control,
  });

  const [totalTreatmentVolume, setTotalTreatmentVolume] = useState(0);

  const drugBatchFieldArray = useFieldArray({
    control,
    name: "drug_batches",
  });

  const drugBatchesFieldArrayTmp = useFieldArray({
    control: modalControl,
    name: "drug_batches_tmp",
  });

  useEffect(() => {
    if (!protocol_id?.value) return;

    const prevProtocolId = prevProtocolIdRef.current;
    const currentProtocolId = protocol_id.value;

    const protocolChanged =
      prevProtocolId !== null && prevProtocolId !== currentProtocolId;

    // aggiorna il ref
    prevProtocolIdRef.current = currentProtocolId;

    // 🔹 CASO 1: EDIT + protocol NON cambiato → usa treatments esistenti
    if (id && !protocolChanged) {
      const tmpOps = [];
      const seenSubstanceId = new Set();

      for (const s of (operation?.treatments || [])) {
        if (seenSubstanceId.has(s.substance_id)) continue;

        seenSubstanceId.add(s.substance_id);

        tmpOps.push({
          ...s,
          product: s?.substance_id,
          product_name: s?.substance_name,
          opType: "PROTOCOL",
          operation: s?.operation || s?.type,
        });
      }
      setOperationsP(tmpOps);
      return;
    }

    // 🔹 CASO 2: protocol CAMBIATO (anche in edit) → OVERRIDE
    const ops =
      [...(protocol_id?.drugs || [])].map((op) => {
        const drug = drugs?.find((d) => d?.id === op?.substance_id);

        const product =
          drug && {
            ...drug,
            value: drug.id,
            label: `${drug.substance_name}${drug.unit ? ` (${drug.unit})` : ""}`,
          };

        return {
          ...op,
          value: op?.substance_id || op?.product,
          product,
          unit_id: drug?.unit_id,
          opType: "PROTOCOL",
        };
      }) || [];

    setOperationsP(ops);
  }, [protocol_id?.value, id]);

  const setModifyMode = (row_id) => {
    setIsModifyMode(row_id);
  };

  const handleOpenModal = (isModifyId = -1, modalType) => {
    modalClearErrors();

    setIsBatchSelectorModal(modalType);
    setIsModalOpen(true);
    setModifyMode(isModifyId);

    if (batch_id_in?.color === "WHITE") {
      if (isModifyId === -1) {
        reset({
          drug_batches_tmp: [],
        });
      } else if (isModifyId > 0) {
        const drugBatches = getValues("drug_batches") || [];

        const tmp = drugs?.find((cc) => cc.id === isModifyId) || {};
        const unit = units?.find((u) => u.id === tmp?.unit_id)?.unit || "";
        const substance = {
          ...tmp,
          unit: unit,
          substance_id: tmp?.id,
          drug_batches: drugBatches?.[isModifyId] || [],
        };
        const groupedTreatmentsBySubstanceId = groupBySubstanceId([substance]); // trick to use operation?.treatments
        const d = groupedTreatmentsBySubstanceId?.[0] || null;
        
        if (d && (d?.drug_batches || [])?.length > 0) {
          setValue("drug_batches_tmp", d?.drug_batches);
        }
      }
    }
  };

  const columnHelper = createColumnHelper();

  const columns = [
    columnHelper.accessor((row) => row?.substance_name || "", {
      header: getLabel("drugName"),
      cell: TableCell,
      meta: {
        type: "text",
        isFilterDisabled: true,
      },
    }),
    columnHelper.accessor(
      (row) =>
        row?.type ? getLabel(`drugType_${row.type?.toUpperCase()}`) : "",
      {
        header: getLabel("drugType"),
        cell: TableCell,
        meta: {
          type: "text",
          isFilterDisabled: true,
        },
      }
    ),
    columnHelper.accessor(
      (row) =>
        row?.recommended_quantity
          ? `${row?.recommended_quantity} ${row?.unit || ""}`
          : "",
      {
        header: getLabel("drugIdealQuantity"),
        cell: TableCell,
        meta: {
          isFilterDisabled: true,
        },
      }
    ),
    columnHelper.accessor(
      (row) =>
        `${row?.min_range ? String(row?.min_range) : "n/a"} - ${
          row?.max_range ? String(row?.max_range) : "n/a"
        } ${row?.unit || ""}`,
      {
        header: getLabel("substanceRange"),
        cell: TableCell,
        meta: {
          type: "number",
          isFilterDisabled: true,
        },
      }
    ),
    columnHelper.accessor(
      (row) => `${row?.quantity || ""}`,
      {
        header: getLabel("drugQuantity"),
        cell: TableCell,
        meta: {
          type: "number",
          editable: true,
          isFilterDisabled: true,
          customClassName: "highlight",
          regExpPattern: INT_FLOAT_REGEX,
          register: register,
          additional: "unit",
        }
      }
    ),
    columnHelper.accessor("batch_row", {
      header: getLabel("drugBatchNumber"),
      cell: TableCell,
      meta: {
        type: "select",
        control: control,
        editable: true,
        isFilterDisabled: true,
        dateFormat: configs?.shortDateFormat,
        options: drugs,
        options2: getValues("drug_batches") || [],
      },
    }),
    columnHelper.display({
      id: "edit",
      cell: EditCell,
      meta: {
        isEditable: true,
      },
    })
  ];

  useEffect(() => {
    const totalVolume =
      dest_tanks?.reduce((sum, b) => sum + (Number(b?.quantity) || 0), 0) || 0;
    if (totalVolume) setTotalTreatmentVolume(totalVolume);
  }, [dest_tanks]);

  const {
    fields,
    append,
    prepend,
    remove,
    swap,
    move,
    insert,
    replace,
    update,
  } = useFieldArray({
    control,
    name: "dest_tanks",
  });

  const loadOperations = async () => {
    const loadedOperations = await getOperations(axiosPrivate);
    dispatch(setOperations(loadedOperations));
  };

  const loadUnits = async () => {
    const currentUnits = await getUnits(axiosPrivate);
    dispatch(setUnits(currentUnits));
  };

  const loadDrugs = async () => {
    const currentDrugs = await getDrugs(axiosPrivate);
    dispatch(setDrugs(currentDrugs));
  };

  const loadTreatments = async () => {
    const currentTreatments = await getProtocols("treatment", axiosPrivate);
    dispatch(setTreatments(currentTreatments));
  };

  const loadWineLabels = async () => {
    const currentWineLabels = await getWineLabels(axiosPrivate);
    dispatch(setWineLabels(currentWineLabels));
  };

  useEffect(() => {
    if (type !== "DESTEMMING") return;

    const fetchPomaces = async () => {
      const pomaces = await getPomacesList(axiosPrivate);
      setPomacesRecipients(pomaces);
    };
    fetchPomaces();
  }, []);


  useEffect(() => {
    initializeProtocolId(operation);
  }, [operation?.protocol_id, protocols]);

  useEffect(() => {
    if (!wineLabels || wineLabels.length === 0) {
      loadWineLabels();
    }
    if (!drugs || drugs.length === 0) {
      loadDrugs();
    }
    if (!protocols || protocols.length === 0) {
      loadTreatments();
    }
    if (!units || units.length === 0) {
      loadUnits();
    }

    if (!id && type === "DESTEMMING") {
      setValue("pomaces.reason", {
        label: getLabel("pomaceReason_STEM"),
        value: "STEM",
      });
      setValue("pomacesW.reason", {
        label: getLabel("pomaceReason_SKIN"),
        value: "SKIN",
      });

      const tmpLotTypeMap = {};
      for (const l of lotNames) {
        if (l?.state === "WINE") {
          tmpLotTypeMap[l?.name] = l?.batch_type; // === 0; // true = PRESSED
        }
      }

      setLotTypeMap(tmpLotTypeMap);
    }

    if (id && (!operations || operations?.length === 0)) loadOperations();
    if (id && operations?.length > 0) {
      const operation = operations?.find((o) => o.id === Number(id)) || null;
      if (operation === null) return;

      const lot = lots?.find((l) => l.id === operation?.batch_id_in);
      const isPlannedByOtherOps =
        lot?.is_planned && lot?.shared_operation_ids?.length > 0
          ? id
            ? lot.shared_operation_ids.some(
                (opId) => Number(opId) !== Number(id)
              )
            : true
          : false;

      setValue("batch_id_in", {
        value: lot?.id,
        label: lot?.name,
        color: lot?.color,
        initial_quantity: lot?.quantity,
        quantity: lot?.actual_quantity,
        planned_quantity: lot?.planned_quantity,
        planned_pomaces: lot?.planned_pomaces,
        shared_operation_ids: lot?.shared_operation_ids,
        composition: lot?.composition,
        is_planned: isPlannedByOtherOps,
        arrow_quantity: getTotalQuantity(true),
      });

      // const chosenQuantity = operation?.dest_tanks?.reduce((acc, curr) => {
      //   return acc + (Number(curr?.quantity) || 0);
      // }, 0);
      // setValue("batch_id_in_quantity", chosenQuantity);

      setValue("priority", {
        label: getLabel(`operationPriority${operation?.priority}`),
        value: operation?.priority,
      });

      // setValue("batchType", {
      //   label: getLabel(`lotType${operation?.batch_type}`),
      //   value: operation?.batch_type,
      // });

      setDestTanks();

      // setNewBatchNames(computeLotNames());

      if (operation?.type === "DESTEMMING") {
        // console.log('operation?.treatments', operation?.treatments)
        const tmpPomaces = operation?.pomaces?.[0] || {};
        const pomaces = {
          quantity: tmpPomaces?.quantity,
          cost: tmpPomaces?.cost,
          close_notes: tmpPomaces?.notes || "",
        };
        setValue("pomaces", pomaces);
        setValue("pomaces.dest", {
          value: tmpPomaces?.id,
          label: tmpPomaces?.recipient,
          is_field: false,
        });
        setValue("pomaces.reason", {
          value: tmpPomaces?.waste_type,
          label: getLabel(`pomaceReason_${tmpPomaces?.waste_type}`),
        });

        if (lot?.color === "WHITE") {
          initializeProtocolId(operation);
          initializeDrugBatches(operation);
          initializeWhitePomaces(operation);
        }
      }
      const cellarmans = [];
      operation?.cellarman_ids?.map((u) => {
        const user = users?.find((us) => us.id === u);
        cellarmans.push({
          value: u,
          label: user?.username,
        });
      });
      setValue("cellarman_ids", cellarmans);

      setValue("expire_date", operation?.expire_date);

      setValue("note", operation?.note);
    }
    if (!id) {
      const savedInputLot = localStorage.getItem("selectedInputLotOperation");
      if (savedInputLot) {
        const { type, id } = JSON.parse(savedInputLot);
        const lot = lots?.find((l) => l?.id === Number(id));

        if (type === "lot" && id && lot) {
          setValue("batch_id_in", {
            value: lot?.id,
            label: lot?.name,
            color: lot?.color,
            initial_quantity: lot?.quantity,
            quantity: lot?.actual_quantity,
            planned_quantity: lot?.planned_quantity,
            shared_operation_ids: lot?.shared_operation_ids,
            composition: lot?.composition,
            is_planned: lot?.is_planned,
            arrow_quantity: getTotalQuantity(true),
          });

          localStorage.removeItem("selectedInputLotOperation");
        }
      }
    }
  }, [operations, operation, lots]);

  const buildPomace = (pomaceData) => ({
    quantity: Number(pomaceData?.quantity) || 0,
    cost: Number(pomaceData?.cost) || 0,
    recipient_id: pomaceData?.dest?.value || null,
    recipient: pomaceData?.dest?.label || null,
    is_new: pomaceData?.dest?.is_new || false,
    is_field: pomaceData?.dest?.is_field,
    waste_type: pomaceData?.reason?.value || null,
    notes: pomaceData?.close_notes,
  });

  const initializeDrugBatches = (operation) => {
    const tmpSubstances = drugs?.map((s) => {
      return {
        ...s,
        removed:
          operation?.treatments?.find(
            (dis) =>
              dis?.substance_id == s?.substance_id && dis?.removed === true
          ) !== undefined,
      };
    });
    tmpSubstances?.forEach((s, i) =>
      s?.removed ? setValue(`removed.${s?.substance_id}.value`, true) : null
    );

    const uniqueTreatments = (operation?.treatments || [])?.map(s => { return {
        substance_id: s?.substance_id,
        unit: s?.unit,
        unit_id: s?.unit_id,
        drug_batches: s?.drug_batches?.map(b => { return {
          ...b,
          value: b?.batch_number,
          label: b?.batch_number,
        }}) || [],
      }}
    );
    
    const groupedTreatmentsBySubstanceId = groupBySubstanceId(uniqueTreatments); // trick to use operation?.treatments        
    for (const t of groupedTreatmentsBySubstanceId) {
      if ((t?.drug_batches || [])?.length > 0) {
        setValue(`drug_batches.${t?.substance_id}`, t?.drug_batches);
      }
    }
  }

  const initializeWhitePomaces = (operation) => {
    const tmpPomacesW = operation?.pomaces?.[1] || {};
    const pomacesW = {
      quantity: tmpPomacesW?.quantity,
      cost: tmpPomacesW?.cost,
      close_notes: tmpPomacesW?.notes || "",
    };
    setValue("pomacesW", pomacesW);
    setValue("pomacesW.dest", {
      value: tmpPomacesW?.id,
      label: tmpPomacesW?.recipient,
      is_field: false,
    });
    setValue("pomacesW.reason", {
      value: tmpPomacesW?.waste_type,
      label: getLabel(`pomaceReason_${tmpPomacesW?.waste_type}`),
    });
  }

  const initializeProtocolId = (operation) => {
    if (operation?.protocol_id && Array.isArray(protocols)) {
      const chosenProtocol = protocols.find(
        (p) =>
          p?.type === "TREATMENT" &&
          p?.protocols_id === operation?.protocol_id
      );

      if (chosenProtocol) {
        const p = {
          ...chosenProtocol,
          value: chosenProtocol.protocols_id,
          label: chosenProtocol.protocols_name,
          name: chosenProtocol.protocols_name,
        };
        setValue("protocol_id", p);
      }
    }
  }

  const customIdMissingDests = "custom-id-missing-dests";
  const sendData = async () => {
    if (Object.keys(errors)?.length > 0) return;
    const data = getValues();
    
    // if (!data?.batch_name_selector?.value) {
    //   data.new_batch_name = data?.batch_name_selector?.label;
    // }
    // return;

    const dests = [];
    const sorted_dest_tanks = dest_tanks
      ?.sort(function (a, b) {
        return b?.batch_name_selector?.value - a?.batch_name_selector?.value;
      })
      ?.filter(
        (d) => d?.tankData?.value && d?.quantity
        // &&
        // ((d?.batch_name_selector === false &&
        //   d?.new_batch_name === "" &&
        //   d?.tankData?.is_planned &&
        //   Number(d?.tankData?.planned_quantity) > 0) || // when extending a planned batch
        //   (d?.batch_name_selector?.value === true && d?.new_batch_name) ||
        //   (d?.batch_name_selector?.value === false &&
        //     d?.batch_name_selector?.label))
      );

    for (const destT of sorted_dest_tanks) {
      const tankData = destT?.tankData;

      // const isPlanned =
      //   tankData?.value &&
      //   tankData?.is_planned_by_other_ops &&
      //   Number(tankData?.quantity) === 0 &&
      //   // Number(tankData?.planned_quantity) === 0 &&
      //   tankData?.is_planned;

      const isPlanned = tankData?.is_planned;

      // const new_batch_name2 = isPlanned
      //   ? tankData?.planned_batch_name
      //   : destT?.new_batch_name;

      const batch_name_label = destT?.batch_name_selector?.label;
      const batch_name_value = destT?.batch_name_selector?.value;
      const new_batch_name = batch_name_value
        ? destT?.new_batch_name
        : batch_name_label || tankData?.planned_batch_name;

      const isPlannedByOtherOps =
        tankData?.is_planned &&
        ((id === null && tankData?.programmed_as_dest?.length > 0) ||
          (id !== null &&
            tankData?.programmed_as_dest?.some((p_id) => p_id !== Number(id))));

      const new_name = (
        isPlanned && isPlannedByOtherOps
          ? tankData?.planned_batch_name
          : batch_name_value
          ? new_batch_name
          : batch_name_label
      )?.trim();

      // console.log('destT', destT, 'tankData', tankData, 'isPlanned', isPlanned, 'isPlannedByOtherOps', isPlannedByOtherOps, 'new_name', new_name);

      const plannedLot =
        lots?.find((l) => l?.name === new_name && l?.status === "PLANNED") ||
        null;
      // const isPlannedByOtherOps = plannedLot
      //   ? plannedLot?.shared_operation_ids?.some(
      //       (opId) => Number(opId) !== Number(id)
      //     )
      //   : false;
      // const isPlannedByOtherOps =
      //   tankData?.is_planned && plannedLot?.shared_operation_ids?.length > 0
      //     ? id
      //       ? plannedLot.shared_operation_ids.some((opId) => Number(opId) !== Number(id))
      //       : true
      //     : false;

      // TO DO isPlannedByOtherOps in CutForm is like isPlannedByOtherOps2 (non funziona se id === null ma uso un lotto giá programmato)

      const isPlannedByOtherOps2 =
        id && tankData?.is_planned
          ? plannedLot?.shared_operation_ids?.some(
              (opId) => Number(opId) !== Number(id)
            ) || false
          : tankData?.is_planned && tankData?.planned_batch_name
          ? true
          : false;

      // if (tankData?.value === 89) console.log(' id && tankData?.is_planned', (tankData?.is_planned && tankData?.planned_batch_name) ? true : false)

      const destLot =
        lots?.find((l) => l?.name === new_name && l.state === "MUST") || null;
      const type = destLot?.id && !isPlannedByOtherOps ? "OLD" : "NEW";

      if (type === null) {
        toast.error(getLabel("toast_invalidType"), {
          toastId: customIdInvalidLotName,
          position: toast.POSITION.BOTTOM_RIGHT,
          exclude: true,
          autoClose: 5000,
        });
        return;
      }
      // const newName = tankData?.is_planned ? tankData?.planned_batch_name :
      //   destT?.batch_name_selector?.value === true
      //     ? destT?.new_batch_name
      //     : destT?.batch_name_selector?.label;

      // if (type === "NEW" && !newBatchIds?.includes(newName)) {
      // newBatchIds.push(newName);
      // TO DO check all cases
      // }

      if (String(new_name)?.length === 0) {
        console.error("Invalid new_name");
        return;
      }

      const t = tanks?.find((t) => t.id === tankData?.value) || {};
      const dest = {
        tank_id: tankData?.value,
        quantity: Number(destT.quantity),
        is_new:
          destT?.batch_name_selector?.value === true &&
          destT?.new_batch_name?.length > 0 &&
          lots?.find((l) => l?.name === new_name && l?.state === "MUST")?.id ===
            undefined,
        new_batch_name: new_name,
        new_batch_id: destLot?.id || null,
        // new_batch_type: type,
        old_batch_id: t?.batch_id || "",
        old_quantity: t?.quantity || 0,
        new_quantity:
          (Number(t?.quantity || 0) || 0) + (Number(destT.quantity) || 0),
        ...(batch_id_in?.color === "WHITE" && {
          batch_type: lotTypeMap?.[new_batch_name],
        }),
          
      };
      dests.push(dest);
    }
    // console.log("dests", dests);
    // return;

    if (dests?.length === 0) {
      toast.error(getLabel("toast_missingDestTanks"), {
        toastId: customIdInvalidLotName,
        position: toast.POSITION.BOTTOM_RIGHT,
        exclude: true,
        autoClose: 5000,
      });
      return;
    }

    const tmpDate = new Date(data?.expire_date)?.setHours(23, 59, 59);
    const info = batch_id_in?.color === "WHITE" ? {
      "analysisType": "tank",
      "isGrapeAnalysis": false,
      "protocolFrequency": "spot"
    }: null;
    const requestPayload = {
      ...(id && { id: id }),
      src_tanks: [
        {
          color: data?.batch_id_in?.color,
          old_quantity: data?.batch_id_in?.quantity,
          name: data?.batch_id_in?.label,
          batch_id: data?.batch_id_in?.value,
          ...(info && { info }),
        },
      ],
      dest_tanks: dests,
      priority: data?.priority.value,
      // pomaces: data?.pomaces,
      // batch_type: data?.batchType?.value,
      // expire_date: addMinutes(
      //   new Date(data?.expire_date)?.setHours(23, 59, 59),
      //   -new Date(data?.expire_date).getTimezoneOffset()
      // ).toISOString(),
      is_exact_date: isExactDate,
      expire_date: new Date(tmpDate)?.toISOString(),
      type: data?.type,
      winery_id: activeWinery?.id,
      cellarman_ids: data?.cellarman_ids?.map((cellMan) => cellMan?.value),
      // new_batch_name: data?.new_batch_name,
      batch_id_in: data?.batch_id_in?.value,
      note: data?.note,
    };
    if (type === "DESTEMMING") {
      const tmpPomaces = [];
      if (data?.pomaces) {
        tmpPomaces.push(buildPomace(data.pomaces));
      }
      if (data?.pomacesW) {
        tmpPomaces.push(buildPomace(data.pomacesW));
      }

      requestPayload.pomaces = tmpPomaces;

      if (batch_id_in?.color === "WHITE") {
        const analysisType = {
          value: "tank"
        };
        const treatments_data = buildTreatmentsDataArray(drugs, drug_batches, data, protocols, protocol_id, "TREATMENT", id, operation, operations, analysisType);
    
        requestPayload.protocol_id = protocol_id?.value;
        requestPayload.treatments_data = treatments_data;
      }
    
    }
      
    console.log("requestPayload", requestPayload);
    // return;
    const toastId = toast.loading(getLabel("toast_inProgress"), {
      type: toast.TYPE.INFO,
      position: toast.POSITION.BOTTOM_RIGHT,
      exclude: true,
    });
    let response = null;

    if (id && !isDuplicate) {
      response = await updateOperation(id, requestPayload, axiosPrivate);
    } else {
      response = await newOperation(requestPayload, axiosPrivate);
    }
    console.log(response);
    toast.update(toastId, {
      render: response?.success
        ? getLabel(response?.success, { type: operation?.type || "" })
        : getLabel(response?.error),
      type:
        !response || response?.error ? toast.TYPE.ERROR : toast.TYPE.SUCCESS,
      isLoading: false,
      position: toast.POSITION.BOTTOM_RIGHT,
      autoClose: 4000,
    });

    if (response && response?.success) {
      const updated_op = response?.updated_data || null;
      // dispatch(empty());
      updateStore(updated_op);
      navClickHandler();
    }
  };

  const updateStore = (payloadOp) => {
    // const tankIds = [...payload?.src_tanks, ...payload?.dest_tanks]?.map(t => t?.tank_id || null)?.filter(Boolean); // remove nulls
    if (id && !isDuplicate) {
      dispatch(updateOp(payloadOp));
    } else {
      dispatch(add(payloadOp));
    }
    dispatch(emptyLots());
    dispatch(emptyTanks());

    // if (payload.id) {
    //   dispatch(emptyLots());
    // } else {
    //   dispatch(add(payload));
    // }
  };

  const navClickHandler = () => {
    if (id) {
      navigate(`/programming/destemming/${id}`);
    } else {
      navigate("/programming");
    }
  };

  const getDrug = () => {
    const tmp = drugs?.find((d) => d?.id === Number(isModifyMode)) || {};
    const drug = { ...tmp, substance_name: tmp?.name };
    return drug;
  };

  const handleCloseModal = () => {
    setIsOpen(false);
    setIsModalOpen(false);
    if (isBatchSelectorModal === "BATCH_SELECTOR") {
      modalReset({
        drug_batches_tmp: [],
      });
    }
  };

  const handleConfirmModal = () => {
    if (isBatchSelectorModal === "BATCH_SELECTOR") {
      const drug = drugs?.find((d) => d?.id === isModifyMode);
      if (drugBatchesFieldArrayTmp.fields?.length > 0) {
        const batches =
          drugBatchesFieldArrayTmp.fields?.map((b) => {
            return { ...b, substance_id: drug?.id };
          }) || [];

        const usedTotal =
          batches.reduce((sum, b) => sum + (Number(b.quantity) || 0), 0) || 0;

        const warehouseUnit = getWarehouseUnit(drug, getLabel);
        const requiredNumericQty = computeRequiredNumericQty(
          drug,
          totalTreatmentVolume
        );
        const requiredWarehouseQty = computeRequiredWarehouseQty(
          warehouseUnit,
          requiredNumericQty
        );
        const remainingQty = Math.max(requiredWarehouseQty - usedTotal, 0);
        const isEnoughStock = usedTotal >= requiredWarehouseQty;
        batches[0].is_enough_stock = isEnoughStock && remainingQty <= 0;

        setIsEnoughStock(isEnoughStock && remainingQty <= 0);
        setValue(`drug_batches.${isModifyMode}`, batches);
      }
       modalReset({
        drug_batches_tmp: [],
      });
    }

    setIsModalOpen(false);
  };

  const getTotalQuantity = (isOperation = false) => {
    const batch_id_in = getValues("batch_id_in") || {};
    if (
      !id &&
      !isOperation &&
      batch_id_in?.is_planned &&
      Number(batch_id_in?.planned_quantity) <= 0
    )
      return 0; // i.e. if the in batch is all planned in other dests

    return (
      (isOperation ? operation?.dest_tanks : dest_tanks)?.reduce(
        (acc, curr) => {
          return acc + (Number(curr?.quantity) || 0);
        },
        0
      ) || 0
    );
  };

  const getWineLabelsOptions = () => {
    if (!wineLabels || wineLabels?.length === 0) return [];
    if (
      getValues("label") &&
      getValues("label")?.filter((l) => l?.type !== "label")?.length > 0
    )
      return wineLabels?.labels;
    return [...wineLabels?.wine, ...wineLabels?.labels] || [];
  };

  const customIdInvalidColor = "custom-id-invalid-color";
  const customIdInvalidLotName = "custom-id-invalid-lot-name";
  const customIdInvalidQuantities = "custom-id-invalid-quantities";
  const openFormModal = () => {
    // console.log('errors', errors)
    // trigger();

    const expDate = getValues("expire_date");
    const tmpDate = new Date(expDate)?.setHours(23, 59, 59);
    if (!expDate || !tmpDate || new Date(tmpDate) < new Date()) {
      setError("expire_date", {
        type: "manual",
        message: getLabel("errorDateInThePast"),
      });
      return;
    }

    if (dest_tanks?.filter((t) => t?.tankData?.value)?.length === 0) {
      toast(getLabel("toast_destemmingMissingDests"), {
        toastId: customIdMissingDests,
        type: toast.TYPE.ERROR,
        isLoading: false,
        position: toast.POSITION.BOTTOM_RIGHT,
      });
      return;
    } else {
      toast.dismiss(customIdMissingDests);
    }

    if (
      dest_tanks?.filter(
        (t) =>
          (!t?.batch_name_selector?.value &&
            t?.batch_name_selector?.label?.length === 0) ||
          (t?.batch_name_selector?.value === true &&
            t?.new_batch_name?.length === 0)
      )?.length > 0
    ) {
      toast(getLabel("toast_destemmingMissingLotSelectors"), {
        toastId: customIdMissingDests,
        type: toast.TYPE.ERROR,
        isLoading: false,
        position: toast.POSITION.BOTTOM_RIGHT,
      });
      return;
    } else {
      toast.dismiss(customIdMissingDests);
    }

    // const filteredTanks = dest_tanks?.filter((d) => d?.tankData?.value);
    // if (filteredTanks?.length === 1)
    //   setValue(
    //     "dest_tanks.0.quantity",
    //     getTotalQuantity()
    //   );

    const destemmingLiters = getTotalQuantity();
    // return;
    const allocated = getTotalQuantity();
    const allocatedInitialQuantity = id ? getTotalQuantity(id > 0) : 0;

    const lot = getValues("batch_id_in");
    const availableForCurrentOp =
      allocatedInitialQuantity + (Number(lot?.planned_quantity) || 0);

    // The current operation can only remove as much as is available
    const totalKgAvailable = Math.min(availableForCurrentOp, allocated); //Number(lot?.quantity);

    if (destemmingLiters <= totalKgAvailable) {
      toast.dismiss(customIdInvalidQuantities);
      // setIsOpen(true);
    } else {
      toast(
        getLabel("toast_destemmingInvalidQuantities", {
          sum: destemmingLiters || "0",
          total: totalKgAvailable,
        }),
        {
          toastId: customIdInvalidQuantities,
          type: toast.TYPE.ERROR,
          isLoading: false,
          position: toast.POSITION.BOTTOM_RIGHT,
        }
      );

      dest_tanks?.forEach((_, index) =>
        setError(`dest_tanks.${index}.quantity`, {
          type: "manual",
          message: getLabel("toast_invalidQuantity"),
        })
      );
      return;
    }

    dest_tanks?.forEach((tank) => {
      if (
        tank?.tankData?.color &&
        tank?.tankData?.color !== getValues("batch_id_in")?.color
      ) {
        toast.warn(getLabel("toast_WarningDiffentWineColor"), {
          toastId: customIdInvalidColor,
          position: toast.POSITION.BOTTOM_RIGHT,
          exclude: true,
          autoClose: 5000,
        });
        return false;
      }
    });

    setIsOpen(true);
  };

  const getLotData = (lot) => {
    const pomaces = Number(getValues("pomaces")?.quantity) || 0;
    const pomacesW = Number(getValues("pomacesW")?.quantity) || 0;
    const pomacesQuantity = pomaces + pomacesW;
    const allocatedInitialQuantity = id ? getTotalQuantity(id > 0) : 0;
    // Calculate the quantity available for consumption in the current operation:
    // This is the difference between the real (current) quantity and what remains planned after scheduled operations.

    // Get the total allocated quantity across destination tanks (dynamic value)
    const allocated = getTotalQuantity();
    const availableForCurrentOp =
      allocatedInitialQuantity + (Number(lot?.planned_quantity) || 0);

    // The current operation can only remove as much as is available
    const checkedQuantity = allocated > availableForCurrentOp ? 0 : allocated; // avoid value over the available quantity
    const arrowQuantity = id
      ? checkedQuantity
      : Math.min(availableForCurrentOp, allocated);
    // Build the lotData object
    const lotData = {};
    lotData.name = lot?.name || lot?.label || "";
    lotData.color = lot?.color;
    lotData.initial_quantity = lot?.initial_quantity; // Fixed initial capacity (e.g., 100 Kg)

    // Set arrow_quantity: the amount to be removed by the current operation
    lotData.arrow_quantity = arrowQuantity;

    const isPlannedByOtherOps =
      lot?.is_planned && lot?.shared_operation_ids?.length > 0
        ? id
          ? lot.shared_operation_ids.some((opId) => Number(opId) !== Number(id))
          : true
        : false;

    const plannedPomacesOtherOps = isPlannedByOtherOps
      ? lot?.planned_pomaces
          ?.filter((t) => (id ? t?.operation_id !== Number(id) : true))
          ?.reduce((acc, curr) => acc + (Number(curr?.quantity) || 0), 0) || 0
      : 0;

    lotData.is_planned = isPlannedByOtherOps; //lot?.is_planned;
    // Set the planned quantity for the current operation:
    // This represents how much will be consumed (arrowQuantity) and thus how much is 'planned' for removal.
    // (If no operation is executed, lot.quantity remains unchanged.)
    // In an editing context (when 'id' exists), we update values based on current distribution.
    if (id) {
      if (availableForCurrentOp > 0) {
        // For display when editing:
        // - The new "quantity" (i.e. what is scheduled to remain) is the planned_quantity from the lot.
        // - The amount consumed by the current operation is arrowQuantity.
        lotData.quantity = Number(lot?.planned_quantity) || 0; // What is expected to remain after scheduled ops
        lotData.planned_quantity = arrowQuantity; // Amount to be removed by the current op

        // For additional display details:
        // "real_initial_quantity" shows the total available for consumption (availableForCurrentOp)
        // "estimated_initial_quantity" shows what remains if we subtract the arrowQuantity from the current quantity.
        lotData.real_initial_quantity = !isPlannedByOtherOps
          ? Number(lot?.quantity)
          : availableForCurrentOp - plannedPomacesOtherOps;

        lotData.estimated_initial_quantity = !isPlannedByOtherOps
          ? 0
          : Number(lot?.quantity) - availableForCurrentOp;

        // Final quantity calculations after the current operation:
        // The real final quantity would be the current lot.quantity minus the amount removed
        lotData.real_final_quantity =
          (!isPlannedByOtherOps
            ? Number(lot?.quantity)
            : availableForCurrentOp) -
          arrowQuantity -
          pomacesQuantity -
          plannedPomacesOtherOps;
        lotData.estimated_final_quantity = !isPlannedByOtherOps
          ? 0
          : Number(lot?.quantity) - availableForCurrentOp;
      } else {
        // If nothing is available, all values become zero.
        lotData.quantity = 0;
        lotData.planned_quantity = 0;
        lotData.real_initial_quantity = 0;
        lotData.estimated_initial_quantity = 0;
        lotData.real_final_quantity = 0;
        lotData.estimated_final_quantity = 0;
      }
    } else {
      // For non-editing contexts, simply use the lot values.
      lotData.quantity = Number(lot?.quantity) || 0;
      lotData.planned_quantity = Number(lot?.planned_quantity) || 0;
      lotData.real_initial_quantity = Number(lot?.quantity) || 0;
      lotData.estimated_initial_quantity =
        (Number(lot?.quantity) || 0) - arrowQuantity;
      lotData.real_final_quantity =
        (isPlannedByOtherOps //lot?.is_planned
          ? (Number(lot?.planned_quantity) || 0) - arrowQuantity
          : !id
          ? Number(lot?.quantity) - arrowQuantity
          : Number(lot?.quantity)) -
        pomacesQuantity -
        plannedPomacesOtherOps;
      // lotData.batch_name = lot?.name || lot?.label || "";
      lotData.estimated_final_quantity = Number(lot?.planned_quantity) || 0;
    }

    lotData.composition = lot?.composition;

    return lotData;
  };

  const getTankData = (t) => {
    const tank = t?.tankData;
    const quantity =
      tank?.availability >= Number(t?.quantity) && Number(t?.quantity) >= 0
        ? Number(t?.quantity)
        : 0;
    const new_batch_name =
      t?.batch_name_selector?.value === true && t?.new_batch_name
        ? t.new_batch_name
        : t?.batch_name_selector?.value === false
        ? t?.batch_name_selector?.label
        : "";

    const tankData = {};

    tankData.arrow_quantity = !id ? quantity : Number(t?.quantity);
    tankData.capacity = tank?.capacity;
    tankData.type = tank?.type;
    tankData.barrels_num = tank?.barrels_num;
    tankData.is_src = false;
    tankData.old_quantity = tank?.quantity;
    tankData.old_batch_name = tank?.lot || "";
    tankData.quantity = tank?.quantity + quantity;
    tankData.color = tank?.is_planned
      ? tank?.planned_batch_color
      : tank?.quantity > 0
      ? tank?.color
      : getValues("batch_id_in")?.color;

    tankData.is_planned = tank?.is_planned;
    tankData.is_planned_state = tank?.is_planned_state;
    tankData.planned_quantity = tank?.is_planned
      ? Number(tank?.planned_quantity) - (!id ? Number(tank?.quantity) : 0)
      : 0;
    tankData.planned_batch_name = tank?.planned_batch_name;
    tankData.planned_batch_type = tank?.planned_batch_type;

    if (
      tank?.planned_batch_name &&
      tank?.is_planned_by_other_ops &&
      !t?.batch_name_selector?.value
    ) {
      tankData.batch_name = tank?.planned_batch_name || new_batch_name;
      if (id) {
        tankData.batch_name = new_batch_name;
        // tankData.batch_type = tank?.batch_type;
        tankData.planned_quantity =
          tankData.planned_quantity - tankData?.old_quantity;
      }
    } else {
      tankData.batch_name = new_batch_name;
      tankData.old_batch_name = tank?.lot;
    }

    return tankData;
  };

  const setDestTanks = () => {
    // const tmpIsPressed = [];
    const dests = [];

    const tmpLotTypeMap = {};
    operation?.dest_tanks?.map((s) => {
      const tank = tanks?.find((t) => t.id === s.tank_id);

      // const plannedLot = lots?.find(
      //   // planned only by the current op, so the lot name can be updated
      //   (l) => l?.id === s?.batch_id && l?.status === "PLANNED"
      // );
      // const isPlannedByOtherOps =
      //   tank?.is_planned && plannedLot?.shared_operation_ids?.length > 0
      //     ? id
      //       ? plannedLot.shared_operation_ids.some((opId) => Number(opId) !== Number(id))
      //       : true
      //     : false;
      const isPlannedByOtherOps =
        tank?.is_planned &&
        ((id === null && tank?.programmed_as_dest?.length > 0) ||
          (id !== null &&
            tank?.programmed_as_dest?.some((p_id) => p_id !== Number(id))));

      // if (s?.tank_id == 88) console.log('isPlannedByOtherOps', s, plannedLot)
      // if same batch_name as src, simulate OLD because it inherits the src name
      const is_new =
        operation?.src_tanks?.[0]?.name === s?.batch_name ? false : s?.is_new;
      const d = {
        quantity: Number(s?.arrow_quantity),
        tankData: {
          value: tank?.id,
          name: tank?.name,
          type: tank?.type,
          barrels_num: tank?.barrels_num,
          label: tank?.name,
          is_src: false,
          capacity: Number(tank?.capacity),
          is_planned_by_other_ops: isPlannedByOtherOps,
          planned_quantity: isPlannedByOtherOps
            ? Number(tank?.planned_quantity) - Number(s?.arrow_quantity)
            : 0,
          is_planned: isPlannedByOtherOps,
          is_planned_state: tank?.is_planned_state,
          planned_batch_name: isPlannedByOtherOps
            ? tank?.planned_batch_name
            : "",
          planned_batch_type: tank?.planned_batch_type,
          quantity: Number(tank?.quantity),
          availability:
            Number(tank?.capacity) -
            Number(tank?.planned_quantity) +
            Number(s?.arrow_quantity),
          color: tank?.color,
          lot:
            lots?.find((lot) => lot?.id === tank?.batch_id)?.name ||
            getLabel("tankState_EMPTY"),
          programmed_as_src: tank?.programmed_as_src,
          programmed_as_dest: tank?.programmed_as_dest,
          is_planned_by_other_ops:
            tank?.is_planned &&
            tank?.programmed_as_dest?.filter(
              (p_id) => id === null || p_id !== Number(id)
            )?.length > 0,
        },
        new_batch_name: s?.batch_name || "",
        batch_name_selector: is_new
          ? {
              value: true,
              label: getLabel("lotNewName"),
            }
          : {
              value: false,
              label: s?.batch_name,
            },
      };
      dests.push(d);

      // 1 = FLOWER, 0 = PRESSED → se è FLOWER, metti false → quindi sarà batch_type: 1
      // Se è PRESSED, metti true → batch_type: 0
      tmpLotTypeMap[tank?.batch_name || tank?.planned_batch_name] =
        s?.batch_type // true = PRESSED
      // tmpIsPressed.push(s?.batch_type);
    });

    replace(dests);

    setLotTypeMap(tmpLotTypeMap);

    // setIsPressed(tmpIsPressed);
  };

  const resetDestTanks = (value) => {
    const initialBatchId = operation?.src_tanks?.[0]?.batch_id || null;
    const newBatchId = value?.value || null;

    // reset dest_tanks when changing src batch (otherwise max and min in dest_tanks do not works)
    if (
      dest_tanks?.length > 0 &&
      dest_tanks?.[0]?.tankData?.value &&
      (!id ||
        (id && newBatchId && initialBatchId && newBatchId !== initialBatchId))
    ) {
      replace(
        dest_tanks?.map((d) => {
          return {
            ...d,
            quantity: 0,
            new_batch_name: "",
            batch_name_selector: false,
          };
        })
      );
    } else if (
      dest_tanks?.length > 0 &&
      dest_tanks?.[0]?.tankData?.value &&
      id
    ) {
      setDestTanks();
    }
  };

  if (
    (
      lots?.filter(
        (lot) => lot?.state === "GRAPE" //&& lot?.id !== getValues("batch_id_in")?.value
      ) || []
    )?.length === 0
  ) {
    return (
      <div className="protocols_alert">
        <h6 className="protocols_alert_title">
          {getLabel("noGrapeLotsAlert")}
        </h6>
        <CTA onClick={() => navigate("/lots/new")}>{getLabel("createLot")}</CTA>
      </div>
    );
  }

  const getSrcTanksForRecap = () => {
    const src_tank = getLotData(getValues("batch_id_in"));

    return [src_tank];
  };

  const getDestTanksForRecap = () => {
    const isDestemming = type === "DESTEMMING";
    const batch_id_in = getValues("batch_id_in") || {};
    
    const dests = dest_tanks?.map((d, index) => {
      const new_batch_name = 
          d?.batch_name_selector?.value === true
            ? d?.new_batch_name
            : d?.batch_name_selector?.label || d?.tankData?.planned_batch_name;
      return {
        ...d?.tankData,
        batch_type: lotTypeMap?.[new_batch_name],
        state: batch_id_in?.color === "WHITE" && isDestemming ? "WINE"
          : batch_id_in?.state || Number(d?.tankData?.quantity) > 0
            ? d?.tankData?.state
            : null,
        name: d?.tankData?.label,
        color: d?.tankData?.color || getValues("batch_id_in")?.color,
        add_quantity: Number(d?.quantity),
        old_quantity: Number(d?.quantity) + Number(d?.tankData?.quantity),
        planned_quantity:
          d?.tankData?.is_planned && d.tankData?.is_planned_by_other_ops
            ? Number(d?.tankData?.planned_quantity) -
              Number(d?.tankData?.quantity)
            : 0,
        quantity: Number(d?.quantity) + Number(d?.tankData?.quantity),
        batch_name: new_batch_name,
        is_planned:
          d?.tankData?.is_planned && d.tankData?.is_planned_by_other_ops,
        // batch_name2: d?.tankData?.is_planned ? d?.tankData?.planned_batch_name :
        //   d?.batch_name_selector?.value === true
        //     ? d?.new_batch_name
        //     : d?.batch_name_selector?.label,
      };
    });

    if (isDestemming) {
      const pomaces = getValues("pomaces");
      const p = computePomace(pomaces);
      // add pomaces as a dest tank with quantity and name only
      if (p) dests.push(p);

      if (batch_id_in?.color === "WHITE") {
        const pomacesW = getValues("pomacesW");
        const pW = computePomace(pomacesW);
        // add pomaces as a dest tank with quantity and name only
        if (pW) dests.push(pW);
      }

    }
    return dests;
  };

  const computePomace = (pomaces) => {
    const pomaces_name = pomaces?.dest?.label || "";
    const pomaces_quantity = Number(pomaces?.quantity) || 0;
    if (pomaces_quantity <= 0 || !pomaces_name || pomaces_name?.length === 0)
      return null;
    return {
        type: "pomaces",
        waste_type: pomaces?.reason?.value || null,
        name: pomaces_name,
        color: batch_id_in?.color,
        quantity: pomaces_quantity,
        add_quantity: pomaces_quantity,
        state: batch_id_in?.state,
        batch_name: pomaces?.dest,
      };
  }

  const getTreatmentsProtocols = () => {
    return (
      protocols
        ?.filter((p) => p?.type === "TREATMENT")
        ?.map((p) => {
          return {
            ...p,
            value: p?.protocols_id,
            label: p?.protocols_name,
            name: p?.protocols_name,
          };
        }) || []
    );
  };

  return (
    <div className="operation_form_wrapper">
      <form
        onSubmit={handleSubmit(openFormModal)}
        autoComplete="off"
        noValidate
      >
        <SourceSelector
          id={id}
          type={type}
          operation={operation}
          control={control}
          errors={errors}
          tanks={tanks}
          lots={lots}
          tank_id_in={batch_id_in}
          dest_tanks={dest_tanks}
          getTankData={getTankData}
          isMobile={isMobile}
          lotNames={lotNames}
          pomaces_quantity={pomaces?.quantity}
          pomaces_w_quantity={pomacesW?.quantity}
        />
        {/* <h5>{getLabel("fromLot")}</h5>
        <div className="operation_form_wrapper_row">
          <Select
            onTableChange={(value) => resetDestTanks(value)}
            control={control}
            defaultValue={operation?.batch_id_in}
            name="batch_id_in"
            label={getLabel("operationDestemmingLot")}
            placeholder={getLabel("operationDestemmingLotPlaceholder")}
            error={errors}
            required={getLabel("inputRequiredError")}
            options={lots
              ?.filter(
                (lot) =>
                  lot?.state === "GRAPE" &&
                  lot?.id !== getValues("batch_id_in")?.value
              )?.sort((a, b) => {
                // last: disabled
                if (a.is_disabled && !b.is_disabled) return 1;
                if (!a.is_disabled && b.is_disabled) return -1;

                // top: fa-calendar icon
                if (a.icon === "fa-calendar" && b.icon !== "fa-calendar") return -1;
                if (a.icon !== "fa-calendar" && b.icon === "fa-calendar") return 1;

                // case insensitive
                return (a.name || "")?.localeCompare(b.name || "", undefined, {
                  numeric: true,
                  sensitivity: "base", // ignore upper/lowercase and accents
                });
              })
              ?.map?.((lot) => {
                return {
                  value: lot.id,
                  label: lot.name,
                  color: lot.color,
                  composition: lot?.composition,
                  initial_quantity: lot?.quantity,
                  quantity: lot?.actual_quantity,
                  planned_quantity: lot?.planned_quantity,
                  shared_operation_ids: lot?.shared_operation_ids,
                  is_planned: lot?.is_planned,
                  is_disabled:
                    !id &&
                    lot?.is_planned &&
                    Number(lot?.planned_quantity) <= 0, // i.e. if the in batch is all planned in other dests
                  icon:
                    (lot?.is_planned && Number(lot?.planned_quantity) >= 0) ||
                    lot?.is_planned_src > 0
                      ? "fa-calendar"
                      : null,
                };
              })}
            tooltip={getLabel("destemmingBatchInTooltip")}
          />
          {getValues("batch_id_in") && (
            <>
              <DestLotDetail
                lot={getLotData(getValues("batch_id_in"))}
                isRecap={false}
                isMobile={isMobile}
                isName={false}
                isAdditional={true}
              />
            </>
          )}
        </div> */}
        <DestinationSelector
          id={id}
          type={type}
          operation={operation}
          register={register}
          control={control}
          errors={errors}
          clearErrors={clearErrors}
          setError={setError}
          trigger={trigger}
          setValue={setValue}
          getValues={getValues}
          tanks={tanks}
          lots={lots}
          tank_id_in={batch_id_in}
          fields={fields}
          lotNames={lotNames}
          // dest_tanks={dest_tanks}
          append={append}
          update={update}
          remove={remove}
          getTankData={getTankData}
          isMobile={isMobile}
          watchedArray={dest_tanks}
          lotTypeMap={lotTypeMap}
          setLotTypeMap={setLotTypeMap}
        />

        {type === "DESTEMMING" && (
          <PomacesDestination
            id={id}
            type={type}
            operation={operation}
            dest_tanks={dest_tanks}
            tank_id_in={batch_id_in}
            register={register}
            control={control}
            errors={errors}
            getLabel={getLabel}
            getValues={getValues}
            setValue={setValue}
            trigger={trigger}
            clearErrors={clearErrors}
            pomacesRecipients={pomacesRecipients}
            setPomacesRecipients={setPomacesRecipients}
          />
        )}
        {type === "DESTEMMING" && batch_id_in?.color === "WHITE" && (
          <PomacesDestination
            id={id}
            type={type}
            operation={operation}
            dest_tanks={dest_tanks}
            tank_id_in={batch_id_in}
            register={register}
            control={control}
            errors={errors}
            getLabel={getLabel}
            getValues={getValues}
            setValue={setValue}
            trigger={trigger}
            clearErrors={clearErrors}
            pomacesRecipients={pomacesRecipients}
            setPomacesRecipients={setPomacesRecipients}
            isWhite={true}
          />
        )}
        
        {type === "DESTEMMING" && batch_id_in?.color === "WHITE" && (
          <div>
            <h6>{getLabel("operationDestemmingPressingDrugs")}</h6>
            <div className="operation_form_wrapper_row align_bottom">
              <Select
                label={getLabel("protocolName")}
                placeholder={getLabel("protocolNamePlaceholder")}
                errors={errors[`protocol_id`]}
                required={getLabel("protocolNameRequiredError")}
                name={`protocol_id`}
                control={control}
                options={getTreatmentsProtocols()}
              />
              <div className="automatic_lot_assignment">
                <Tooltip
                  variant={"info"}
                  html={getLabel("treatmentAssignDrugBatchesTooltip")}
                  place="right"
                  events={["hover"]}
                >
                  <CTA
                    // customClassName={"dialog confirm"}
                    disabled={!protocol_id?.value}
                    isRed={false}
                    onClick={() =>
                      assignDrugBatches(
                        operationsP,
                        totalTreatmentVolume,
                        drugBatchFieldArray,
                        getLabel,
                        null,
                        true
                      )
                    }
                  >
                    {getLabel("treatmentAssignDrugBatches")}
                  </CTA>
                </Tooltip>
              </div>
            </div>
            {protocol_id?.value && (
              <div className="operation_form_wrapper_row vertical">
                <TableEdit
                  defaultData={operationsP}
                  columns={columns}
                  // errors={errors}
                  errors={modalErrors}
                  control={modalControl}
                  isEditable={true}
                  isLegend={false}
                  isSearchBar={false}
                  // setValue={setValue}
                  setValue={setValue}
                  getValues={getValues}
                  type={"treatment"}
                  modalHandler={handleOpenModal}
                  setModifyMode={setModifyMode}
                  setRows={setOperationsP}
                  totalVolume={totalTreatmentVolume}
                />
              </div>
            )}
          </div>
        )}

        <h6>{getLabel("operationAdditional")}</h6>
        <div className="operation_form_wrapper_row">
          <MultipleSelect
            name={`cellarman_ids`}
            control={control}
            label={getLabel("operationUser")}
            placeholder={getLabel("operationUserPlaceholder")}
            error={errors["cellarman_ids"]}
            required={getLabel("inputRequiredError")}
            options={users?.map?.((user) => {
              return {
                value: user?.id,
                label: user?.username,
              };
            })}
            tooltip={getLabel("destemmingCellarmenTooltip")}
          />
          <Select
            name="priority"
            control={control}
            label={getLabel("operationPriority")}
            placeholder={getLabel("operationPriorityPlaceholder")}
            error={errors}
            required={getLabel("inputRequiredError")}
            options={[
              {
                label: getLabel("lotQuality_HIGH"),
                value: "2",
              },
              {
                label: getLabel("lotQuality_MEDIUM"),
                value: "1",
              },
              {
                label: getLabel("lotQuality_LOW"),
                value: "0",
              },
            ]}
            // defaultValue={{
            //     value: "",
            //     label: "",
            //   }}
          />
          <DatePicker
            name="expire_date"
            control={control}
            label={getLabel("operationExpireDateForm")}
            error={errors["expire_date"]}
            required={getLabel("inputRequiredError")}
            dateFormat={configs.shortDateFormat}
            placeholder={getLabel("expiringDatePlaceholder")}
          />
          {getValues("expire_date") && (
            <Input
              name={"expire_date_exact"}
              register={register}
              label={getLabel("isExactDate")}
              error={errors["expire_date_exact"]}
              // isLabel={false}
              type={"checkbox"}
              checked={isExactDate}
              onChange={() => setIsExactDate((prev) => !prev)}
              customClassName={"confirm no_margin_top"}
              tooltip={getLabel("destemmingExactDate")}
            />
          )}
        </div>
        <div className="client_form_wrapper_row">
          <TextArea
            name="note"
            register={register}
            label={getLabel("clientNotes")}
            placeholder={getLabel("clientNotesPlaceholder")}
            error={errors["note"]}
          />
        </div>
        <div className="operation_form_wrapper_row">
          <Submit label={getLabel("submitForm")}></Submit>
        </div>
      </form>
      {isBatchSelectorModal !== "BATCH_SELECTOR" && (
        <ConfirmationModal
          isOpen={isOpen}
          onConfirm={sendData}
          onClose={handleCloseModal}
          description={getLabel("modalNewDestemmingDescription", {
            name: operation?.id || "",
          })}
          isOperationRecap={true}
          totalStep={0}
          errors={errors}
          getValues={getValues}
        >
          <OperationRecap
            type={"DESTEMMING"}
            control={control}
            register={register}
            getValues={getValues}
            errors={errors}
            lots={lots}
            lotNames={lots?.map((l) => l?.name)}
            dest_tanks={getDestTanksForRecap()}
            src_tanks={getSrcTanksForRecap()}
          />
        </ConfirmationModal>
      )}
      {isBatchSelectorModal === "BATCH_SELECTOR" && (
        <ConfirmationModal
          isOpen={isModalOpen}
          onConfirm={handleConfirmModal}
          onClose={handleCloseModal}
          description={getLabel("modalConfigureDrugBatchDescription")}
          isOperationRecap={true}
          totalStep={0}
          errors={modalErrors}
          getValues={modalGetValues}
          // configs={configs}
        >
          <OperationRecap
            id={id || null}
            type={"NEW_DRUG_BATCH"}
            configs={configs}
            control={modalControl}
            register={modalRegister}
            getValues={modalGetValues}
            setValue={modalSetValue}
            errors={modalErrors}
            // units={units}
            arrayFields={drugBatchesFieldArrayTmp}
            additionalData={getDrug()}
            additionalSubtype={totalTreatmentVolume}
            isAutomaticCTA={true}
          />
        </ConfirmationModal>
      )}
    </div>
  );
};

export default DestemmingForm;
